import { getFollowUps } from "./followUps";

// Generates an id guaranteed not to collide with any id in `existingIds`,
// and reserves it in that set so a second call in the same batch can't
// generate a duplicate either.
function generateId(existingIds) {
  let id;
  do {
    id = `u${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
  } while (existingIds.has(id));
  existingIds.add(id);
  return id;
}

// Normalizes one raw uploaded record into a player object matching the
// app's schema. Only `name` is required — everything else falls back to
// sensible defaults. Returns null for anything unusable (so the caller can
// skip it and keep processing the rest of the file).
export function normalizeUploadedPlayer(raw, existingIds) {
  if (!raw || typeof raw !== "object") return null;
  const name = typeof raw.name === "string" ? raw.name.trim() : "";
  if (!name) return null;

  const player = {
    id: generateId(existingIds),
    name,
    lastName: typeof raw.lastName === "string" && raw.lastName.trim()
      ? raw.lastName.trim()
      : name.split(" ").slice(-1)[0] || "",
    classification: raw.classification === "Junior" ? "Junior" : "Adult",
    tier: typeof raw.tier === "string" && raw.tier.trim() ? raw.tier.trim() : "No Group",
    age: Number.isFinite(raw.age) ? raw.age : null,
    groupId: null,
    availability: Array.isArray(raw.availability)
      ? raw.availability.filter(a => typeof a === "string")
      : [],
  };
  if (typeof raw.email === "string" && raw.email.trim()) player.email = raw.email.trim();
  // Family grouping for the public schedule lookup — see utils/households.js.
  if (raw.householdId != null && String(raw.householdId).trim()) {
    player.householdId = String(raw.householdId).trim();
  }
  if (raw.gender === "Male" || raw.gender === "Female") player.gender = raw.gender;
  if (typeof raw.skillLevel === "string" && raw.skillLevel.trim()) player.skillLevel = raw.skillLevel.trim();
  if (typeof raw.preferredPartners === "string" && raw.preferredPartners.trim()) player.preferredPartners = raw.preferredPartners.trim();
  if (typeof raw.notes === "string" && raw.notes.trim()) player.notes = raw.notes.trim();
  if (typeof raw.privateTimes === "string" && raw.privateTimes.trim()) player.privateTimes = raw.privateTimes.trim();
  // Follow-up tags survive a re-upload of an exported kac_data.json, so a
  // round-trip through the roster file doesn't quietly empty the queue.
  const followUps = getFollowUps(raw);
  if (followUps.length > 0) player.followUps = followUps;

  return player;
}

// Parses an uploaded JSON file's text content into a batch of new players.
// Accepts either a bare array of player-like objects or an
// { players: [...] } wrapper (so an exported/partial kac_data.json can be
// re-uploaded as-is). `existingIds` should be every player id already in
// the roster, so new ids never collide.
export function parseUploadedPlayers(jsonText, existingIds) {
  let data;
  try {
    data = JSON.parse(jsonText);
  } catch {
    return { error: "Could not parse file — make sure it's valid JSON." };
  }

  const rawList = Array.isArray(data)
    ? data
    : Array.isArray(data?.players)
      ? data.players
      : null;

  if (!rawList) {
    return { error: "Invalid file: expected an array of players, or { players: [] }." };
  }

  const idSet = new Set(existingIds);
  const players = [];
  let skipped = 0;
  rawList.forEach(raw => {
    const player = normalizeUploadedPlayer(raw, idSet);
    if (player) players.push(player);
    else skipped++;
  });

  return { players, skipped, total: rawList.length };
}
